import { CalendarMode, CalendarView, Row } from "PFApp/booking/types";
import { RowType } from "PFApp/booking/types/row";
import { ProjectItem } from "PFCore/hooks/queries/bookings/projects/use_projects_entries";
import { WorkforceItem } from "PFCore/hooks/queries/bookings/workforce/use_workforce_entries";
import { useEffect, useMemo } from "react";

import { useBookingRowsContext } from "../../context/booking_rows_context";
import { useBookingsOverviewContext } from "../../context/bookings_overview_context";
import { checkIsLastLevel } from "../side_panel_row.utils";

type UseOpen = {
  row: Row;
  onCollapse: VoidFunction;
};
type UseOpenReturn = {
  expanded: boolean;
  expandable: boolean;
  setExpanded: (expanded: boolean) => void;
  collapseRow: VoidFunction;
};

type CheckIsExpandable = {
  calendarMode: CalendarMode;
  view: CalendarView;
  row: Row;
};
export const checkIsExpandable = ({ calendarMode, view, row }: CheckIsExpandable): boolean => {
  const { type, original } = row;
  const isWorkforceMode = calendarMode === CalendarMode.Workforce;
  if (isWorkforceMode) {
    if (type !== RowType.Profile || view === CalendarView.BookingsOnly) {
      return false;
    }
    const { profile } = original as WorkforceItem;
    return profile.has_bookings;
  } else {
    const isLastLevel = checkIsLastLevel(CalendarMode.Projects, row);
    if (isLastLevel) {
      return false;
    }
    const { activity } = original as ProjectItem;
    return !!activity?.children_workflow_states_tally || activity?.has_bookings;
  }
};

export const useExpanded = ({ row, onCollapse }: UseOpen): UseOpenReturn => {
  const {
    calendarMode,
    calendarData: { view }
  } = useBookingsOverviewContext();
  const { setRowsByCalendarMode, expandedRowsState, setExpandedRowsState } = useBookingRowsContext();

  const { key } = row;
  const expanded = !!expandedRowsState[calendarMode][key];
  const setExpanded = (expanded: boolean) =>
    setExpandedRowsState((prev) => ({
      ...prev,
      [calendarMode]: { ...prev[calendarMode], [key]: expanded }
    }));

  const collapseRow = () => {
    setRowsByCalendarMode((prevState) => {
      const prevRows = prevState[calendarMode];
      const newRows = prevRows.filter(({ parents }) => !parents?.find((parentKey) => parentKey === key));
      return {
        ...prevState,
        [calendarMode]: newRows
      };
    });
    setExpanded(false);
    onCollapse();
  };

  const expandable = useMemo(() => checkIsExpandable({ calendarMode, view, row }), [row, calendarMode, view]);

  useEffect(() => {
    if (!expandable) {
      setExpanded(false);
    }
  }, [expandable]);

  useEffect(() => {
    if (!expandable) {
      setExpanded(false);
    }
  }, [expandable]);

  return { expanded, expandable, setExpanded, collapseRow };
};
